import { useState } from 'react'
import { Product } from '@/types'
import { getPriceOrPriceRange, formatMoney } from '@/helpers'
import ProductHelpers from '@/helpers/Product'

export interface ProductSelection {
  sku: string,
  upc: string,
  inStock: boolean,
  price: string,
  calculatedPrice: string,
  variantId: number,
  variantImage: string,
  productAttribute: number[],
  updateProductSelection: (quantity: number, attribute: number[]) => void,
  isFreeShipping: boolean,
}

export const useProductSelection = (product: Product): ProductSelection => {
  const [{
    sku, variantId, upc, inStock,
  }, setSku] = useState<{
    sku: string,
    variantId: number,
    upc: string,
    inStock: boolean
  }>({
    sku: '',
    variantId: -1,
    upc: '',
    inStock: false,
  })
  const [calculatedPrice, setCalculatedPrice] = useState<string>(getPriceOrPriceRange(product, 'calculated_price'))
  const [price, setPrice] = useState<string>(getPriceOrPriceRange(product, 'price'))
  const [productAttribute, setProductAttribute] = useState<number[]>([])

  const variant = product.variants
    .find((candidateVariant) => candidateVariant.id === variantId)

  const VariantFromWarranty = product.warranty.variants?.[parseInt(variant?.sku || '-1')]
  const variantImage = variant?.images?.[0]?.url_square || product.images?.[0]?.url_square || ''
  const isFreeShipping = (
    VariantFromWarranty?.is_free_shipping !== undefined ?
      VariantFromWarranty.is_free_shipping :
      product?.is_free_shipping
  )

  const updateProductSelection = (quantity, attribute) => {
    const variantFilters = attribute.reduce((acc, optionValueId, optionId) => ({
      [optionId]: optionValueId,
      ...acc,
    }), {})
    const selectedVariant = product.variants.find(({ option_values }) => {
      let isVariant = true
      option_values.forEach(({ id, option_id }) => {
        if (variantFilters[option_id] !== id) {
          isVariant = false
        }
      })
      return isVariant
    })

    const selectedVariantPrice = product?.prices?.variants.find(({ id }) => (
      id === selectedVariant?.id
    ))

    setSku({
      sku: selectedVariant?.sku || '',
      variantId: selectedVariant?.id || -1,
      inStock: (selectedVariant?.inventory_level && selectedVariant?.inventory_level > 0) || false,
      upc: selectedVariant?.upc || '',
    })

    if (!ProductHelpers.getCollectionVariants(product).length) {
      setPrice(formatMoney(selectedVariantPrice?.price))
      setCalculatedPrice(formatMoney(selectedVariantPrice?.calculated_price))
    }

    setProductAttribute(attribute)
  }

  return {
    sku,
    upc,
    inStock,
    price,
    calculatedPrice,
    variantId,
    variantImage,
    productAttribute,
    updateProductSelection,
    isFreeShipping,
  }
}
